import { PresentationRecord, presentationFromJSON } from "common/records/Presentation";
import { RootActions, RootState } from "common/reducers";
import {
  PresentationActions,
  EDIT_PRESENTATION,
  EDIT_PRESENTATIONS,
  FETCH_PRESENTATION,
  FETCH_PRESENTATIONS,
  RECEIVE_PRESENTATION,
  RECEIVE_PRESENTATIONS,
  SET_CURRENT_PRESENTATION_ID,
  SET_PRESENTATIONS_LOADED,
  SET_PRESENTATION_LOADED,
  REMOVE_PRESENTATION,
  RESET_PRESENTATION,
  SET_LOADING,
  ADD_SLIDE,
  REMOVE_SLIDE,
  ADD_SLIDES,
  REMOVE_SLIDES,
  JOB_PRESENTATION_SORT_END,
  INITIALIZE_JOB_PRESENTATION,
} from "common/reducers/presentation.actions";
import { Nullable } from "common/types";
import { List, Map, Record } from "immutable";
import { fetch, loaded, receive, reset } from "common/reducers/Utils";
import { reducer as slideReducer } from "common/reducers/slide.reducer";
import { SlideActions } from "common/reducers/slide.actions";
import { reducer as idsManagementReducer } from "common/reducers/idsManagement.reducer";
import { IdsManagementActions } from "common/reducers/idsManagement.actions";
import { addSlide, addSlides, moveSlide, removeSlide, removeSlides } from "common/services/presentationBuilder.service";
import { DynamicPresentationLinkRecord } from "common/records/DynamicPresentationLink";
import { fromJSON as dplFromJSON } from "common/records/DynamicPresentationLink";

export const PresentationStateRecord = Record({
  byId: Map<number, PresentationRecord>(),
  lastSavedById: Map<number, PresentationRecord>(),
  // Presentation ID of the presentation being presented
  currentPresentationId: null as Nullable<number>,
  loading: false,
});

export const initialState = PresentationStateRecord();
export type PresentationState = typeof initialState;
export const model = "presentations";

export const reducer = (state: RootState, action: RootActions): RootState => {
  if (state && !state.get(model)) {
    state = state.set(model, initialState);
  }

  switch (action.type) {
    case SET_CURRENT_PRESENTATION_ID:
      return state.setIn([model, "currentPresentationId"], action.payload.id);

    case FETCH_PRESENTATION:
      return fetch(state, model, presentationFromJSON({ id: action.payload.id })) as RootState;

    case RECEIVE_PRESENTATION: {
      const slides = action.payload.presentation.slides;
      if (slides) {
        state = slideReducer(state, SlideActions.receiveSlides(slides));
      }

      return receive(state, model, presentationFromJSON(action.payload.presentation)) as RootState;
    }

    case SET_PRESENTATION_LOADED:
      return loaded(state, model, action.payload.id) as RootState;

    case EDIT_PRESENTATION:
      return state.setIn([model, "byId", action.payload.id, action.payload.name], action.payload.value);

    case RESET_PRESENTATION:
      return reset(state, model, action.payload.id) as RootState;

    case FETCH_PRESENTATIONS:
      action.payload.ids.forEach((id) => {
        state = reducer(state, PresentationActions.fetchPresentation(id));
      });

      return state;

    case RECEIVE_PRESENTATIONS:
      action.payload.presentations.forEach((d) => {
        state = reducer(state, PresentationActions.receivePresentation(d));
      });

      return state;

    case SET_PRESENTATIONS_LOADED:
      action.payload.ids.forEach((id) => {
        state = reducer(state, PresentationActions.setPresentationLoaded(id));
      });

      return state;

    case REMOVE_PRESENTATION:
      state = idsManagementReducer(state, IdsManagementActions.removeId(action.payload.id, model));

      return state.deleteIn([model, "byId", action.payload.id]);

    case EDIT_PRESENTATIONS:
      action.payload.ids.forEach((id) => {
        state = reducer(state, PresentationActions.editPresentation(id, action.payload.name, action.payload.value));
      });

      return state;

    case SET_LOADING:
      return state.setIn([model, "loading"], action.payload.loading);

    case ADD_SLIDE:
      const { dynamicPresentation, slideId, orgPresentations, sortOrder = "default" } = action.payload;
      const updatedRecord = addSlide(state, dynamicPresentation, slideId, orgPresentations, sortOrder);

      return state.setIn([model, "byId", dynamicPresentation.id], updatedRecord);

    case REMOVE_SLIDE: {
      const { dynamicPresentation, slideId } = action.payload;
      const updatedRecord = removeSlide(dynamicPresentation, slideId);

      return state.setIn([model, "byId", dynamicPresentation.id], updatedRecord);
    }

    case ADD_SLIDES: {
      const { dynamicPresentation, presentation, orgPresentations, sortOrder = "default" } = action.payload;
      const updatedRecord = addSlides(state, dynamicPresentation, presentation, orgPresentations, sortOrder);

      return state.setIn([model, "byId", dynamicPresentation.id], updatedRecord);
    }

    case REMOVE_SLIDES: {
      const { dynamicPresentation, presentation } = action.payload;
      const updatedRecord = removeSlides(dynamicPresentation, presentation);

      return state.setIn([model, "byId", dynamicPresentation.id], updatedRecord);
    }

    case JOB_PRESENTATION_SORT_END: {
      const { presentation, oldIndex, newIndex } = action.payload;
      const slide = presentation.dynamic_links.get(oldIndex);
      if (!slide) {
        console.error("Slide not found for sorting", oldIndex, newIndex, presentation.dynamic_links);
        return state;
      }
      const newLinks = moveSlide(presentation.dynamic_links, slide, oldIndex, newIndex);
      return state.setIn([model, "byId", presentation.id, "dynamic_links"], newLinks);
    }

    case INITIALIZE_JOB_PRESENTATION: {
      const { jobId, inspectionId } = action.payload;
      let links = List<DynamicPresentationLinkRecord>();
      links = links.push(
        dplFromJSON({
          presentable_id: inspectionId,
          presentable_type: "Estimate",
          kind: "inspection",
          sort_order: 0,
        }),
      );

      const p = presentationFromJSON({
        id: -1,
        name: "Job Presentation",
        job_id: jobId,
        kind: "dynamic",
        dynamic_links: links.map((dl) => dl.toJS()).toArray() as any,
        slides: [],
      });

      return state.setIn([model, "byId", p.id], p);
    }

    default:
      return state;
  }
};
